import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Link as LinkIcon } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    InputAdornment,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useIndividualPlanQueries } from '../../hooks/useIndividualPlanQueries';

interface IndividualPlanTabProps {
    userId: string;
}

const IndividualPlanTab: React.FC<IndividualPlanTabProps> = ({ userId }) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
    const [priceInput, setPriceInput] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [localError, setLocalError] = useState<string | null>(null);
    const [copySuccess, setCopySuccess] = useState(false);

    const {
        individualPlan,
        isLoading: loading,
        error: queryError,
        setIndividualPlan: setIndividualPlanMutation,
        deleteIndividualPlan: deleteIndividualPlanMutation,
        isSettingPlan: saving,
        isDeletingPlan: deleting,
        setError: setMutationError,
        deleteError: deleteMutationError,
    } = useIndividualPlanQueries(userId);

    // Combine query error and local error for display
    const error = localError ||
        (queryError && !queryError.message.includes('404') ? 'Failed to load individual plan' : null) ||
        (setMutationError ? 'Failed to save individual plan' : null) ||
        (deleteMutationError ? 'Failed to delete individual plan' : null);

    const handleOpenDialog = (mode: 'create' | 'edit') => {
        setDialogMode(mode);
        if (mode === 'edit' && individualPlan) {
            // Convert from cents to euros for display
            setPriceInput((individualPlan.price / 100).toFixed(2));
        } else {
            setPriceInput('');
        }
        setDialogOpen(true);
        setLocalError(null);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setPriceInput('');
        setLocalError(null);
    };

    const handleSave = () => {
        setLocalError(null);

        const priceValue = parseFloat(priceInput);
        if (isNaN(priceValue) || priceValue <= 0) {
            setLocalError('Please enter a valid price greater than 0');
            return;
        }

        // Convert euros to cents
        const priceInCents = Math.round(priceValue * 100);

        setIndividualPlanMutation(
            { price: priceInCents },
            {
                onSuccess: () => {
                    handleCloseDialog();
                },
                onError: (error: any) => {
                    setLocalError(error.response?.data?.error || 'Failed to save individual plan');
                },
            }
        );
    };

    const handleDelete = () => {
        setLocalError(null);

        deleteIndividualPlanMutation(undefined, {
            onSuccess: () => {
                setDeleteDialogOpen(false);
            },
            onError: (error: any) => {
                setLocalError(error.response?.data?.error || 'Failed to delete individual plan');
            },
        });
    };

    const formatPrice = (priceInCents: number) => {
        return `${(priceInCents / 100).toFixed(2)}`;
    };

    const handleCopyLink = async () => {
        // Build dynamic link based on current environment
        const baseUrl = `${window.location.protocol}//${window.location.host}`;
        const link = `${baseUrl}/plans?individual-payment-link`;

        try {
            await navigator.clipboard.writeText(link);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000); // Reset after 2 seconds
        } catch (error) {
            console.error('Failed to copy link:', error);
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = link;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {individualPlan ? (
                <Card>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Typography variant="h6" component="h3">
                                Individual Plan Configuration
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<EditIcon />}
                                    onClick={() => handleOpenDialog('edit')}
                                    size="small"
                                >
                                    Edit Price
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    startIcon={<DeleteIcon />}
                                    onClick={() => setDeleteDialogOpen(true)}
                                    size="small"
                                >
                                    Delete
                                </Button>
                                <Button
                                    variant="outlined"
                                    color={copySuccess ? "success" : "primary"}
                                    startIcon={<LinkIcon />}
                                    onClick={handleCopyLink}
                                    size="small"
                                >
                                    {copySuccess ? 'Copied!' : 'Copy Link'}
                                </Button>
                            </Box>
                        </Box>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Price
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                        {formatPrice(individualPlan.price)}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Currency
                                    </Typography>
                                    <Typography variant="body2">{individualPlan.currency}</Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            ) : (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No Individual Plan Configured
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        This user doesn't have a custom individual plan price set.
                    </Typography>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog('create')}>
                        Create Individual Plan
                    </Button>
                </Paper>
            )}

            {/* Price Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {dialogMode === 'create' ? 'Create Individual Plan' : 'Edit Individual Plan Price'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1 }}>
                        <TextField
                            fullWidth
                            label="Price"
                            type="number"
                            value={priceInput}
                            onChange={(e) => setPriceInput(e.target.value)}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">€</InputAdornment>,
                            }}
                            helperText="Enter the price in euros (e.g., 499.99)"
                            inputProps={{
                                min: 0,
                                step: 0.01,
                            }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={saving || !priceInput}
                        startIcon={saving ? <CircularProgress size={16} /> : null}
                    >
                        {saving ? 'Saving...' : dialogMode === 'create' ? 'Create' : 'Update'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Individual Plan</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete the individual plan for this user? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button
                        onClick={handleDelete}
                        color="error"
                        variant="contained"
                        disabled={deleting}
                        startIcon={deleting ? <CircularProgress size={16} /> : null}
                    >
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default IndividualPlanTab;
